import "./CompanyBanks.css";
import JournalAuditModal from "./components/JournalAuditModal";
import { usePermissions } from "./PermissionContext";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const formatDateInput = (value = new Date()) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const moneyOrDash = (value) =>
  value === null || value === undefined ? "—" : money(value);

const formatDate = (value) => {
  if (!value) return "";

  return new Date(`${value}T00:00:00`).toLocaleDateString("es-CO");
};

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
};

const emptyAccount = {
  bank_name: "",
  account_type: "checking",
  account_number: "",
  account_holder: "",
  accounting_account_id: "",
  initial_balance: "",
  is_active: true,
};

const emptyMovement = {
  movement_date: formatDateInput(),
  movement_type: "income",
  amount: "",
  concept: "",
  reference: "",
  notes: "",
};

const emptyReconciliation = {
  period_start: formatDateInput(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      1
    )
  ),
  period_end: formatDateInput(),
  statement_balance: "",
  statement_opening_balance: "",
  notes: "",
};

const emptyStatementLine = {
  transaction_date: formatDateInput(),
  amount: "",
  description: "",
  reference: "",
};

const emptyScheduledPayment = {
  bank_account_id: "",
  supplier_id: "",
  due_date: formatDateInput(),
  amount: "",
  concept: "",
  reference: "",
  notes: "",
};

const emptyTransfer = {
  source_bank_account_id: "",
  destination_bank_account_id: "",
  transfer_date: formatDateInput(),
  amount: "",
  concept: "",
  reference: "",
  notes: "",
};

function CompanyBanks() {
  const [accounts, setAccounts] = useState([]);
  const [accountingAccounts, setAccountingAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [scheduledPayments, setScheduledPayments] = useState([]);
  const [transferForm, setTransferForm] = useState(emptyTransfer);

  const [savingTransfer, setSavingTransfer] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [movements, setMovements] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [selectedReconciliationId, setSelectedReconciliationId] = useState("");
  const [statementLines, setStatementLines] = useState([]);
  const [ledgerLines, setLedgerLines] = useState([]);
  const [statementMatches, setStatementMatches] = useState([]);
  const [reconciliationSummary, setReconciliationSummary] = useState(null);
  const [statementForm, setStatementForm] = useState(emptyStatementLine);
  const [matchForms, setMatchForms] = useState({});
  const [loadingReconciliationDetail, setLoadingReconciliationDetail] = useState(false);
  const [savingStatementLine, setSavingStatementLine] = useState(false);
  const [savingMatchId, setSavingMatchId] = useState(null);
  const [voidingMatchId, setVoidingMatchId] = useState(null);
  const [completingReconciliation, setCompletingReconciliation] = useState(false);
  const detailRequestId = useRef(0);

  const navigate = useNavigate();
  const { company } = useParams();
  const { can } = usePermissions();

  const canManageBanks = can("banks.manage");
  const canViewAccounting = can("accounting.view");
  const canViewAudit = can("accounting.audit");

  const [linkedJournalEntry, setLinkedJournalEntry] =
    useState(null);
  const [auditJournalEntry, setAuditJournalEntry] =
    useState(null);

  const [accountForm, setAccountForm] = useState(emptyAccount);
  const [movementForm, setMovementForm] = useState(emptyMovement);

  const [reconciliationForm, setReconciliationForm] = useState(
    emptyReconciliation
  );

  const [scheduledForm, setScheduledForm] = useState(
    emptyScheduledPayment
  );

  const [loading, setLoading] = useState(true);
  const [savingAccount, setSavingAccount] = useState(false);
  const [savingMovement, setSavingMovement] = useState(false);

  const [savingReconciliation, setSavingReconciliation] =
    useState(false);

  const [savingScheduled, setSavingScheduled] = useState(false);

  const selectedAccount = useMemo(
    () =>
      accounts.find(
        (account) =>
          Number(account.id) === Number(selectedAccountId)
      ),
    [accounts, selectedAccountId]
  );

  const selectedReconciliation = useMemo(
    () => reconciliations.find(
      (item) => Number(item.id) === Number(selectedReconciliationId)
    ),
    [reconciliations, selectedReconciliationId]
  );

  const activeMatches = useMemo(
    () => statementMatches.filter((item) => !item.voided_at),
    [statementMatches]
  );

  const loadAccounts = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/banks/me/accounts`,
      {
        headers: getHeaders(),
      }
    );

    setAccounts(response.data);

    setSelectedAccountId((current) => {
      if (current) return current;

      return response.data[0]?.id || "";
    });
  }, []);

  const loadAccountingAccounts = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/accounting/accounts`,
      {
        headers: getHeaders(),
        params: {
          only_active: true,
        },
      }
    );

    setAccountingAccounts(
      response.data.filter(
        (account) =>
          account.is_active &&
          account.allows_posting &&
          account.account_type === "ACTIVO"
      )
    );
  }, []);

  const loadSuppliers = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/suppliers/me`,
      {
        headers: getHeaders(),
        params: { active_only: true },
      }
    );

    setSuppliers(response.data);
  }, []);

  const loadScheduledPayments = useCallback(async () => {
    const response = await axios.get(
      `${API_URL}/banks/me/scheduled-payments`,
      {
        headers: getHeaders(),
      }
    );

    setScheduledPayments(response.data);
  }, []);

  const loadAccountData = useCallback(async () => {
    if (!selectedAccountId) {
      setMovements([]);
      setReconciliations([]);
      return;
    }

    const [movementsResponse, reconciliationsResponse] =
      await Promise.all([
        axios.get(
          `${API_URL}/banks/me/accounts/${selectedAccountId}/movements`,
          { headers: getHeaders() }
        ),
        axios.get(
          `${API_URL}/banks/me/accounts/${selectedAccountId}/reconciliations`,
          { headers: getHeaders() }
        ),
      ]);

    setMovements(movementsResponse.data);
    setReconciliations(reconciliationsResponse.data);
  }, [selectedAccountId]);

  const loadReconciliationDetail = useCallback(async (accountId, reconciliationId) => {
    const requestId = ++detailRequestId.current;
    setLoadingReconciliationDetail(true);
    setStatementLines([]);
    setLedgerLines([]);
    setStatementMatches([]);
    setReconciliationSummary(null);
    try {
      const base = `${API_URL}/banks/me/accounts/${accountId}/reconciliations/${reconciliationId}`;
      const [linesResponse, ledgerResponse, matchesResponse, summaryResponse] =
        await Promise.all([
          axios.get(`${base}/statement-lines`, { headers: getHeaders() }),
          axios.get(`${base}/ledger-lines`, { headers: getHeaders() }),
          axios.get(`${base}/matches`, { headers: getHeaders() }),
          axios.get(`${base}/summary`, { headers: getHeaders() }),
        ]);
      if (requestId === detailRequestId.current) {
        setStatementLines(linesResponse.data);
        setLedgerLines(ledgerResponse.data);
        setStatementMatches(matchesResponse.data);
        setReconciliationSummary(summaryResponse.data);
      }
    } finally {
      if (requestId === detailRequestId.current) {
        setLoadingReconciliationDetail(false);
      }
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      await Promise.all([
        loadAccounts(),
        loadAccountingAccounts(),
        loadSuppliers(),
        loadScheduledPayments(),
      ]);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [
    loadAccounts,
    loadAccountingAccounts,
    loadSuppliers,
    loadScheduledPayments,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadAccountData().catch((error) =>
      toast.error(getErrorMessage(error))
    );
  }, [loadAccountData]);

  useEffect(() => {
    detailRequestId.current += 1;
    setSelectedReconciliationId("");
    setStatementLines([]);
    setLedgerLines([]);
    setStatementMatches([]);
    setReconciliationSummary(null);
    setMatchForms({});
  }, [selectedAccountId]);

  useEffect(() => {
    if (
      !selectedAccountId || !selectedReconciliationId ||
      !selectedReconciliation ||
      Number(selectedReconciliation.bank_account_id) !== Number(selectedAccountId)
    ) return;
    loadReconciliationDetail(selectedAccountId, selectedReconciliationId)
      .catch((error) => toast.error(getErrorMessage(error)));
  }, [selectedAccountId, selectedReconciliationId, selectedReconciliation, loadReconciliationDetail]);

  const refreshAll = async () => {
    await loadData();
    await loadAccountData();
    if (selectedAccountId && selectedReconciliationId) {
      await loadReconciliationDetail(selectedAccountId, selectedReconciliationId);
    }
  };

  const handleAccountChange = (event) => {
    const { name, value, type, checked } = event.target;

    setAccountForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAccountSubmit = async (event) => {
    event.preventDefault();
    if (!canManageBanks) {
      toast.error("No tienes permiso para administrar bancos.");
      return;
    }

    if (
      !accountForm.bank_name.trim() ||
      !accountForm.account_number.trim() ||
      !accountForm.account_holder.trim() ||
      !accountForm.accounting_account_id
    ) {
      toast.error("Completa los datos obligatorios de la cuenta.");
      return;
    }

    setSavingAccount(true);

    try {
      await axios.post(
        `${API_URL}/banks/me/accounts`,
        {
          ...accountForm,
          accounting_account_id: Number(
            accountForm.accounting_account_id
          ),
          initial_balance: Number(
            accountForm.initial_balance || 0
          ),
        },
        { headers: getHeaders() }
      );

      toast.success("Cuenta bancaria creada correctamente.");
      setAccountForm(emptyAccount);
      await refreshAll();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingAccount(false);
    }
  };

  const handleMovementChange = (event) => {
    const { name, value } = event.target;

    setMovementForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleMovementSubmit = async (event) => {
    event.preventDefault();
    if (!canManageBanks) {
      toast.error("No tienes permiso para administrar bancos.");
      return;
    }

    if (!selectedAccount) {
      toast.error("Selecciona una cuenta bancaria.");
      return;
    }

    if (
      !movementForm.amount ||
      Number(movementForm.amount) <= 0 ||
      !movementForm.concept.trim()
    ) {
      toast.error("Indica un valor válido y el concepto.");
      return;
    }

    setSavingMovement(true);

    try {
      await axios.post(
        `${API_URL}/banks/me/accounts/${selectedAccount.id}/movements`,
        {
          ...movementForm,
          amount: Number(movementForm.amount),
          concept: movementForm.concept.trim(),
        },
        { headers: getHeaders() }
      );

      toast.success("Movimiento bancario registrado.");
      setMovementForm(emptyMovement);
      await refreshAll();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingMovement(false);
    }
  };

  const handleTransferChange = (event) => {
    const { name, value } = event.target;

    setTransferForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleViewBankTransferJournalEntry = async (
    sourceMovementId
  ) => {
    try {
      const response = await axios.get(
        `${API_URL}/accounting/source-entry/bank_transfer/${sourceMovementId}`,
        {
          headers: getHeaders(),
        }
      );

      setLinkedJournalEntry(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info(
          "La transferencia todavía no tiene un comprobante contable asociado."
        );
        return;
      }

      toast.error(getErrorMessage(error));
    }
  };

  const handleTransferSubmit = async (event) => {
    event.preventDefault();
    if (!canManageBanks) {
      toast.error("No tienes permiso para administrar bancos.");
      return;
    }

    if (
      !transferForm.source_bank_account_id ||
      !transferForm.destination_bank_account_id ||
      !transferForm.amount ||
      Number(transferForm.amount) <= 0 ||
      !transferForm.concept.trim()
    ) {
      toast.error(
        "Completa las cuentas, el valor y el concepto."
      );
      return;
    }

    if (
      Number(transferForm.source_bank_account_id) ===
      Number(transferForm.destination_bank_account_id)
    ) {
      toast.error(
        "La cuenta de origen y destino deben ser diferentes."
      );
      return;
    }

    setSavingTransfer(true);

    try {
      const response = await axios.post(
        `${API_URL}/banks/me/transfers`,
        {
          ...transferForm,
          source_bank_account_id: Number(
            transferForm.source_bank_account_id
          ),
          destination_bank_account_id: Number(
            transferForm.destination_bank_account_id
          ),
          amount: Number(transferForm.amount),
          concept: transferForm.concept.trim(),
          reference: transferForm.reference.trim() || null,
          notes: transferForm.notes.trim() || null,
        },
        { headers: getHeaders() }
      );

      toast.success(
        "Transferencia bancaria registrada correctamente."
      );
      const sourceMovementId =
        response.data?.source_movement_id ??
        response.data?.source_movement?.id;

      if (sourceMovementId) {
        await handleViewBankTransferJournalEntry(
          sourceMovementId
        );
      }

      setTransferForm(emptyTransfer);
      await refreshAll();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingTransfer(false);
    }
  };

  const handleReconciliationChange = (event) => {
    const { name, value } = event.target;

    setReconciliationForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleReconciliationSubmit = async (event) => {
    event.preventDefault();
    if (!canManageBanks) {
      toast.error("No tienes permiso para administrar bancos.");
      return;
    }

    if (!selectedAccount) {
      toast.error("Selecciona una cuenta bancaria.");
      return;
    }

    if (
      reconciliationForm.statement_opening_balance === "" ||
      reconciliationForm.statement_balance === "" ||
      reconciliationForm.period_end < reconciliationForm.period_start
    ) {
      toast.error("Revisa las fechas y los saldos del extracto.");
      return;
    }

    setSavingReconciliation(true);

    try {
      const response = await axios.post(
        `${API_URL}/banks/me/accounts/${selectedAccount.id}/reconciliations`,
        {
          ...reconciliationForm,
          statement_opening_balance: reconciliationForm.statement_opening_balance,
          statement_balance: reconciliationForm.statement_balance,
        },
        { headers: getHeaders() }
      );

      toast.success("Conciliación creada correctamente.");
      setReconciliationForm(emptyReconciliation);
      await loadAccountData();
      setStatementForm({
        ...emptyStatementLine,
        transaction_date: response.data.period_end,
      });
      setSelectedReconciliationId(response.data.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingReconciliation(false);
    }
  };
  const completeReconciliation = async (reconciliation) => {
    if (!canManageBanks || !selectedAccount || completingReconciliation) return;
    const confirmed = window.confirm(
      "¿Cerrar esta conciliación? Las líneas del extracto deben estar totalmente emparejadas. Los apuntes pendientes permanecerán abiertos."
    );

    if (!confirmed) return;

    setCompletingReconciliation(true);
    try {
      await axios.post(
        `${API_URL}/banks/me/accounts/${selectedAccount.id}/reconciliations/${reconciliation.id}/complete`,
        {},
        { headers: getHeaders() }
      );

      toast.success("Conciliación completada.");
      await loadAccountData();
      await loadReconciliationDetail(selectedAccount.id, reconciliation.id);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setCompletingReconciliation(false);
    }
  };

  const getRemainingStatementAmount = (line) => {
    const allocated = activeMatches
      .filter((match) => Number(match.statement_line_id) === Number(line.id))
      .reduce((total, match) => total + Number(match.amount), 0);
    return Number((Number(line.amount) - allocated).toFixed(2));
  };

  const handleStatementLineSubmit = async (event) => {
    event.preventDefault();
    if (!canManageBanks || !selectedAccount || !selectedReconciliation) return;
    const amount = Number(statementForm.amount);
    if (
      !Number.isFinite(amount) || amount === 0 ||
      !statementForm.description.trim() ||
      statementForm.transaction_date < selectedReconciliation.period_start ||
      statementForm.transaction_date > selectedReconciliation.period_end
    ) {
      toast.error("Indica fecha, importe distinto de cero y descripción válidos.");
      return;
    }
    setSavingStatementLine(true);
    try {
      const base = `${API_URL}/banks/me/accounts/${selectedAccount.id}/reconciliations/${selectedReconciliation.id}`;
      await axios.post(`${base}/statement-lines`, {
        transaction_date: statementForm.transaction_date,
        amount: statementForm.amount,
        description: statementForm.description.trim(),
        reference: statementForm.reference.trim() || null,
      }, { headers: getHeaders() });
      setStatementForm({
        ...emptyStatementLine,
        transaction_date: selectedReconciliation.period_end,
      });
      await loadReconciliationDetail(selectedAccount.id, selectedReconciliation.id);
      toast.success("Línea del extracto registrada.");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingStatementLine(false);
    }
  };

  const handleMatchSubmit = async (line) => {
    if (!canManageBanks || !selectedAccount || !selectedReconciliation) return;
    const draft = matchForms[line.id] || {};
    const candidate = ledgerLines.find(
      (item) => Number(item.journal_line_id) === Number(draft.journal_entry_line_id)
    );
    const amount = Number(draft.amount);
    const remaining = getRemainingStatementAmount(line);
    if (
      !candidate || !Number.isFinite(amount) || amount === 0 ||
      amount * remaining <= 0 ||
      amount * Number(candidate.remaining_amount) <= 0 ||
      Math.abs(amount) > Math.abs(remaining) ||
      Math.abs(amount) > Math.abs(Number(candidate.remaining_amount))
    ) {
      toast.error("Selecciona un apunte e importe del mismo sentido, sin exceder los pendientes.");
      return;
    }
    setSavingMatchId(line.id);
    try {
      const base = `${API_URL}/banks/me/accounts/${selectedAccount.id}/reconciliations/${selectedReconciliation.id}`;
      await axios.post(`${base}/statement-lines/${line.id}/matches`, {
        journal_entry_line_id: Number(candidate.journal_line_id),
        amount: draft.amount,
      }, { headers: getHeaders() });
      setMatchForms((current) => ({ ...current, [line.id]: {} }));
      await loadReconciliationDetail(selectedAccount.id, selectedReconciliation.id);
      toast.success("Emparejamiento registrado.");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingMatchId(null);
    }
  };

  const handleVoidMatch = async (match) => {
    if (!canManageBanks || !selectedAccount || !selectedReconciliation) return;
    const reason = window.prompt("Motivo de anulación del emparejamiento:");
    if (reason === null) return;
    if (!reason.trim() || reason.trim().length > 255) {
      toast.error("Indica un motivo de máximo 255 caracteres.");
      return;
    }
    setVoidingMatchId(match.id);
    try {
      const base = `${API_URL}/banks/me/accounts/${selectedAccount.id}/reconciliations/${selectedReconciliation.id}`;
      await axios.post(`${base}/matches/${match.id}/void`, {
        reason: reason.trim(),
      }, { headers: getHeaders() });
      await loadReconciliationDetail(selectedAccount.id, selectedReconciliation.id);
      toast.success("Emparejamiento anulado; el historial se conserva.");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setVoidingMatchId(null);
    }
  };

  const handleScheduledChange = (event) => {
    const { name, value } = event.target;

    setScheduledForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleScheduledSubmit = async (event) => {
    event.preventDefault();
    if (!canManageBanks) {
      toast.error("No tienes permiso para administrar bancos.");
      return;
    }

    if (
      !scheduledForm.bank_account_id ||
      !scheduledForm.amount ||
      Number(scheduledForm.amount) <= 0 ||
      !scheduledForm.concept.trim()
    ) {
      toast.error(
        "Selecciona una cuenta e indica valor y concepto del pago."
      );
      return;
    }

    setSavingScheduled(true);

    try {
      await axios.post(
        `${API_URL}/banks/me/scheduled-payments`,
        {
          ...scheduledForm,
          bank_account_id: Number(
            scheduledForm.bank_account_id
          ),
          supplier_id: scheduledForm.supplier_id
            ? Number(scheduledForm.supplier_id)
            : null,
          amount: Number(scheduledForm.amount),
          concept: scheduledForm.concept.trim(),
        },
        { headers: getHeaders() }
      );

      toast.success("Pago programado correctamente.");
      setScheduledForm(emptyScheduledPayment);
      await loadScheduledPayments();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingScheduled(false);
    }
  };

  const executeScheduledPayment = async (payment) => {
    const confirmed = window.confirm(
      `¿Ejecutar el pago de ${money(payment.amount)}?`
    );

    if (!confirmed) return;

    try {
      await axios.post(
        `${API_URL}/banks/me/scheduled-payments/${payment.id}/execute`,
        {},
        { headers: getHeaders() }
      );

      toast.success(
        "Pago ejecutado y egreso bancario registrado."
      );

      await refreshAll();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const cancelScheduledPayment = async (payment) => {
    try {
      await axios.patch(
        `${API_URL}/banks/me/scheduled-payments/${payment.id}/cancel`,
        {},
        { headers: getHeaders() }
      );

      toast.success("Pago programado cancelado.");
      await loadScheduledPayments();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const getMovementLabel = (type) => {
    const labels = {
      income: "Ingreso",
      expense: "Egreso",
      transfer_in: "Transferencia recibida",
      transfer_out: "Transferencia enviada",
      adjustment_in: "Ajuste positivo",
      adjustment_out: "Ajuste negativo",
    };

    return labels[type] || type;
  };
  const handleViewBankMovementJournalEntry = async (
    movement
  ) => {
    try {
      const response = await axios.get(
        `${API_URL}/accounting/source-entry/bank_movement/${movement.id}`,
        {
          headers: getHeaders(),
        }
      );

      setLinkedJournalEntry(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info(
          "Este movimiento bancario todavía no tiene un comprobante contable asociado."
        );
        return;
      }

      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="company-banks">
      <section className="banks-heading">
        <div>
          <p className="banks-kicker">Tesorería</p>
          <h1>Bancos</h1>
          <p>
            Controla movimientos, conciliaciones y pagos
            programados.
          </p>
        </div>

        <button
          type="button"
          className="btn-bank-refresh"
          onClick={refreshAll}
          disabled={loading}
        >
          {loading ? "Actualizando..." : "Actualizar"}
        </button>
      </section>

      <section className="banks-card bank-account-form-card">
        <div className="bank-card-heading">
          <div>
            <h2>Registrar cuenta bancaria</h2>
            <p>Registra las cuentas que usará la empresa.</p>
          </div>
        </div>
        {canManageBanks ? (
          <form className="bank-form" onSubmit={handleAccountSubmit}>
            <div className="bank-form-grid">
              <label>
                Banco
                <input
                  name="bank_name"
                  value={accountForm.bank_name}
                  onChange={handleAccountChange}
                  placeholder="Nombre del banco"
                  required
                />
              </label>

              <label>
                Tipo de cuenta
                <select
                  name="account_type"
                  value={accountForm.account_type}
                  onChange={handleAccountChange}
                >
                  <option value="checking">Corriente</option>
                  <option value="savings">Ahorros</option>
                  <option value="digital_wallet">
                    Billetera digital
                  </option>
                  <option value="other">Otra</option>
                </select>
              </label>

              <label>
                Número de cuenta
                <input
                  name="account_number"
                  value={accountForm.account_number}
                  onChange={handleAccountChange}
                  placeholder="Número o identificador"
                  required
                />
              </label>

              <label>
                Cuenta contable PUC
                <select
                  name="accounting_account_id"
                  value={accountForm.accounting_account_id}
                  onChange={handleAccountChange}
                  required
                >
                  <option value="">
                    Selecciona una cuenta contable
                  </option>

                  {accountingAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.code} - {account.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Titular
                <input
                  name="account_holder"
                  value={accountForm.account_holder}
                  onChange={handleAccountChange}
                  placeholder="Titular de la cuenta"
                  required
                />
              </label>

              <label>
                Saldo inicial
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  name="initial_balance"
                  value={accountForm.initial_balance}
                  onChange={handleAccountChange}
                  placeholder="0"
                />
              </label>
            </div>

            <label className="bank-check">
              <input
                type="checkbox"
                name="is_active"
                checked={accountForm.is_active}
                onChange={handleAccountChange}
              />
              Cuenta activa
            </label>

            <button
              type="submit"
              className="btn-bank-save"
              disabled={savingAccount}
            >
              {savingAccount ? "Guardando..." : "Crear cuenta"}
            </button>
          </form>
        ) : (
          <div className="bank-readonly-message">
            No tienes permiso para administrar cuentas bancarias.
          </div>
        )}
      </section>

      <section className="bank-accounts-grid">
        {accounts.length === 0 && !loading ? (
          <p className="bank-empty">
            No hay cuentas bancarias registradas.
          </p>
        ) : (
          accounts.map((account) => (
            <button
              type="button"
              key={account.id}
              className={
                Number(selectedAccountId) === account.id
                  ? "bank-account-card selected"
                  : "bank-account-card"
              }
              onClick={() => setSelectedAccountId(account.id)}
            >
              <span>{account.bank_name}</span>
              <strong>{money(account.current_balance)}</strong>
              <small>
                {account.account_type} · {account.account_number}
              </small>
            </button>
          ))
        )}
      </section>

      {selectedAccount && (
        <>
          <section className="banks-card bank-movement-card">
            <div className="bank-card-heading">
              <div>
                <h2>Movimiento bancario</h2>
                <p>
                  Cuenta seleccionada: {selectedAccount.bank_name} —{" "}
                  {selectedAccount.account_number}
                </p>
              </div>

              <span className="bank-current-balance">
                Saldo: {money(selectedAccount.current_balance)}
              </span>
            </div>
            {canManageBanks ? (
              <form className="bank-form" onSubmit={handleMovementSubmit}>
                <div className="bank-form-grid">
                  <label>
                    Fecha
                    <input
                      type="date"
                      name="movement_date"
                      value={movementForm.movement_date}
                      onChange={handleMovementChange}
                      required
                    />
                  </label>

                  <label>
                    Tipo
                    <select
                      name="movement_type"
                      value={movementForm.movement_type}
                      onChange={handleMovementChange}
                    >
                      <option value="income">Ingreso</option>
                      <option value="expense">Egreso</option>
                      <option value="transfer_in">
                        Transferencia recibida
                      </option>
                      <option value="transfer_out">
                        Transferencia enviada
                      </option>
                      <option value="adjustment_in">
                        Ajuste positivo
                      </option>
                      <option value="adjustment_out">
                        Ajuste negativo
                      </option>
                    </select>
                  </label>

                  <label>
                    Valor
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      name="amount"
                      value={movementForm.amount}
                      onChange={handleMovementChange}
                      placeholder="0"
                      required
                    />
                  </label>

                  <label>
                    Referencia
                    <input
                      name="reference"
                      value={movementForm.reference}
                      onChange={handleMovementChange}
                      placeholder="Comprobante opcional"
                    />
                  </label>
                </div>

                <label className="bank-notes">
                  Concepto
                  <input
                    name="concept"
                    value={movementForm.concept}
                    onChange={handleMovementChange}
                    placeholder="Descripción del movimiento"
                    required
                  />
                </label>

                <label className="bank-notes">
                  Observaciones
                  <textarea
                    name="notes"
                    value={movementForm.notes}
                    onChange={handleMovementChange}
                    rows="2"
                  />
                </label>

                <button
                  type="submit"
                  className="btn-bank-save"
                  disabled={savingMovement}
                >
                  {savingMovement
                    ? "Guardando..."
                    : "Registrar movimiento"}
                </button>
              </form>
            ) : (
              <div className="bank-readonly-message">
                No tienes permiso para administrar cuentas bancarias.
              </div>
            )}
          </section>

          <section className="banks-card bank-reconciliation-card">
            <div className="bank-card-heading">
              <div>
                <h2>Conciliación bancaria</h2>
                <p>
                  Compara el saldo del extracto con el saldo
                  registrado.
                </p>
              </div>
            </div>
            {canManageBanks ? (
              <form
                className="bank-form"
                onSubmit={handleReconciliationSubmit}
              >
                <div className="bank-form-grid">
                  <label>
                    Desde
                    <input
                      type="date"
                      name="period_start"
                      value={reconciliationForm.period_start}
                      onChange={handleReconciliationChange}
                      required
                    />
                  </label>

                  <label>
                    Hasta
                    <input
                      type="date"
                      name="period_end"
                      value={reconciliationForm.period_end}
                      onChange={handleReconciliationChange}
                      required
                    />
                  </label>

                  <label>
                    Saldo inicial del extracto
                    <input
                      type="number"
                      step="0.01"
                      name="statement_opening_balance"
                      value={reconciliationForm.statement_opening_balance}
                      onChange={handleReconciliationChange}
                      placeholder="0"
                      required
                    />
                  </label>

                  <label>
                    Saldo según extracto
                    <input
                      type="number"
                      step="0.01"
                      name="statement_balance"
                      value={reconciliationForm.statement_balance}
                      onChange={handleReconciliationChange}
                      placeholder="0"
                      required
                    />
                  </label>
                </div>

                <label className="bank-notes">
                  Observaciones
                  <textarea
                    name="notes"
                    value={reconciliationForm.notes}
                    onChange={handleReconciliationChange}
                    rows="2"
                  />
                </label>

                <button
                  type="submit"
                  className="btn-bank-save"
                  disabled={savingReconciliation}
                >
                  {savingReconciliation
                    ? "Creando..."
                    : "Crear conciliación"}
                </button>
              </form>
            ) : (
              <div className="bank-readonly-message">
                No tienes permiso para administrar cuentas bancarias.
              </div>
            )}

            <div className="bank-table-wrapper">
              <table className="bank-table">
                <thead>
                  <tr>
                    <th>Período</th>
                    <th>Saldo inicial</th>
                    <th>Extracto</th>
                    <th>Libro al crear</th>
                    <th>Diferencia al crear</th>
                    <th>Estado</th>
                    <th />
                  </tr>
                </thead>

                <tbody>
                  {reconciliations.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="bank-empty">
                        No hay conciliaciones registradas.
                      </td>
                    </tr>
                  ) : (
                    reconciliations.map((item) => (
                      <tr key={item.id}>
                        <td>
                          {formatDate(item.period_start)} -{" "}
                          {formatDate(item.period_end)}
                        </td>

                        <td>{item.statement_opening_balance == null ? "—" : money(item.statement_opening_balance)}</td>
                        <td>{money(item.statement_balance)}</td>
                        <td>{money(item.system_balance)}</td>

                        <td
                          className={
                            Number(item.difference) === 0
                              ? "bank-positive"
                              : "bank-negative"
                          }
                        >
                          {money(item.difference)}
                        </td>

                        <td>
                          <span
                            className={`bank-status ${item.status}`}
                          >
                            {item.status === "reconciled"
                              ? "Conciliada"
                              : "Borrador"}
                          </span>
                        </td>

                        <td>
                          <button
                            type="button"
                            className="btn-bank-detail"
                            onClick={() => {
                              setStatementForm({
                                ...emptyStatementLine,
                                transaction_date: item.period_end,
                              });
                              setSelectedReconciliationId(item.id);
                            }}
                            aria-pressed={Number(selectedReconciliationId) === Number(item.id)}
                          >
                            Ver extracto
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {selectedReconciliation && (
              <div className="bank-reconciliation-detail">
                <div className="bank-card-heading">
                  <div>
                    <h3>Extracto del {formatDate(selectedReconciliation.period_start)} al {formatDate(selectedReconciliation.period_end)}</h3>
                    <p>Empareja cada línea con uno o varios apuntes contabilizados. Los apuntes sin pareja quedan pendientes para otro extracto.</p>
                  </div>
                  <button
                    type="button"
                    className="btn-bank-detail"
                    onClick={() => setSelectedReconciliationId("")}
                  >
                    Cerrar detalle
                  </button>
                </div>

                {loadingReconciliationDetail ? (
                  <p>Cargando extracto y apuntes contables...</p>
                ) : (
                  <>
                    {reconciliationSummary && (
                      <>
                        <div className="bank-reconciliation-summary">
                          <div><span>Saldo inicial</span><strong>{moneyOrDash(reconciliationSummary.statement_opening_balance)}</strong></div>
                          <div><span>Movimientos del extracto</span><strong>{money(reconciliationSummary.statement_lines_total)}</strong></div>
                          <div><span>Saldo final declarado</span><strong>{money(reconciliationSummary.statement_balance)}</strong></div>
                          <div><span>Saldo final calculado</span><strong>{moneyOrDash(reconciliationSummary.calculated_statement_balance)}</strong></div>
                          <div><span>Diferencia del extracto</span><strong>{moneyOrDash(reconciliationSummary.statement_difference)}</strong></div>
                          <div><span>Libro contable al corte</span><strong>{money(reconciliationSummary.ledger_balance)}</strong></div>
                          <div><span>Libro menos extracto</span><strong>{money(reconciliationSummary.ledger_minus_statement)}</strong></div>
                          <div><span>Líneas sin emparejar</span><strong>{reconciliationSummary.unmatched_statement_line_count}</strong></div>
                        </div>
                        {Number(reconciliationSummary.ledger_minus_statement) !== 0 && (
                          <p className="bank-reconciliation-notice">
                            El libro y el extracto difieren. Revisa abajo los apuntes pendientes; esta diferencia no impide por sí sola cerrar el extracto.
                          </p>
                        )}
                      </>
                    )}

                    {selectedReconciliation.status === "draft" && canManageBanks && (
                      <form className="bank-form bank-statement-form" onSubmit={handleStatementLineSubmit}>
                        <h4>Añadir línea del extracto</h4>
                        <div className="bank-form-grid">
                          <label>Fecha
                            <input type="date" value={statementForm.transaction_date}
                              onChange={(event) => setStatementForm((current) => ({ ...current, transaction_date: event.target.value }))} required />
                          </label>
                          <label>Importe (+ ingreso / − egreso)
                            <input type="number" step="0.01" value={statementForm.amount}
                              onChange={(event) => setStatementForm((current) => ({ ...current, amount: event.target.value }))} required />
                          </label>
                          <label>Referencia
                            <input type="text" maxLength="120" value={statementForm.reference}
                              onChange={(event) => setStatementForm((current) => ({ ...current, reference: event.target.value }))} />
                          </label>
                          <label>Descripción
                            <input type="text" maxLength="255" value={statementForm.description}
                              onChange={(event) => setStatementForm((current) => ({ ...current, description: event.target.value }))} required />
                          </label>
                        </div>
                        <button type="submit" className="btn-bank-save" disabled={savingStatementLine}>
                          {savingStatementLine ? "Guardando..." : "Agregar línea"}
                        </button>
                      </form>
                    )}

                    <h4>Líneas del extracto</h4>
                    <div className="bank-table-wrapper">
                      <table className="bank-table bank-statement-table">
                        <thead><tr>
                          <th># / Fecha</th><th>Descripción</th><th>Importe</th>
                          <th>Emparejado</th><th>Pendiente</th><th>Vínculos activos</th><th>Emparejar</th>
                        </tr></thead>
                        <tbody>
                          {statementLines.length === 0 ? (
                            <tr><td colSpan="7" className="bank-empty">Aún no hay líneas del extracto.</td></tr>
                          ) : statementLines.map((line) => {
                            const lineMatches = activeMatches.filter(
                              (match) => Number(match.statement_line_id) === Number(line.id)
                            );
                            const remaining = getRemainingStatementAmount(line);
                            const allocated = Number(line.amount) - remaining;
                            const candidates = ledgerLines.filter(
                              (item) => Number(item.remaining_amount) * remaining > 0
                            );
                            const draft = matchForms[line.id] || {};
                            return (
                              <tr key={line.id}>
                                <td>{line.line_number}<br /><small>{formatDate(line.transaction_date)}</small></td>
                                <td>{line.description}<br /><small>{line.reference || "Sin referencia"}</small></td>
                                <td>{money(line.amount)}</td>
                                <td>{money(allocated)}</td>
                                <td className={remaining === 0 ? "bank-positive" : "bank-negative"}>{money(remaining)}</td>
                                <td>
                                  {lineMatches.length === 0 ? "—" : lineMatches.map((match) => (
                                    <div className="bank-match-item" key={match.id}>
                                      <span>Apunte #{match.journal_entry_line_id}: {money(match.amount)}</span>
                                      {selectedReconciliation.status === "draft" && canManageBanks && (
                                        <button type="button" className="btn-bank-cancel"
                                          disabled={voidingMatchId === match.id}
                                          onClick={() => handleVoidMatch(match)}>
                                          Anular
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </td>
                                <td>
                                  {selectedReconciliation.status === "draft" && canManageBanks && remaining !== 0 ? (
                                    <div className="bank-match-form">
                                      <select aria-label={`Apunte para línea ${line.line_number}`}
                                        value={draft.journal_entry_line_id || ""}
                                        onChange={(event) => {
                                          const candidateId = event.target.value;
                                          const candidate = candidates.find(
                                            (item) => Number(item.journal_line_id) === Number(candidateId)
                                          );
                                          const suggested = candidate
                                            ? (Math.sign(remaining) * Math.min(
                                                Math.abs(remaining), Math.abs(Number(candidate.remaining_amount))
                                              )).toFixed(2)
                                            : "";
                                          setMatchForms((current) => ({
                                            ...current,
                                            [line.id]: { journal_entry_line_id: candidateId, amount: suggested },
                                          }));
                                        }}>
                                        <option value="">Selecciona un apunte</option>
                                        {candidates.map((item) => (
                                          <option key={item.journal_line_id} value={item.journal_line_id}>
                                            {item.entry_number} · {formatDate(item.entry_date)} · {money(item.remaining_amount)}
                                          </option>
                                        ))}
                                      </select>
                                      <input type="number" step="0.01" aria-label={`Importe para línea ${line.line_number}`}
                                        value={draft.amount || ""}
                                        onChange={(event) => setMatchForms((current) => ({
                                          ...current,
                                          [line.id]: { ...current[line.id], amount: event.target.value },
                                        }))} />
                                      <button type="button" className="btn-bank-save"
                                        disabled={!draft.journal_entry_line_id || savingMatchId === line.id}
                                        onClick={() => handleMatchSubmit(line)}>
                                        {savingMatchId === line.id ? "Guardando..." : "Vincular"}
                                      </button>
                                    </div>
                                  ) : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <h4>Apuntes contables aún pendientes</h4>
                    <div className="bank-table-wrapper">
                      <table className="bank-table">
                        <thead><tr><th>Fecha</th><th>Asiento</th><th>Descripción</th><th>Tipo</th><th>Pendiente</th></tr></thead>
                        <tbody>
                          {ledgerLines.length === 0 ? (
                            <tr><td colSpan="5" className="bank-empty">No hay apuntes pendientes al corte.</td></tr>
                          ) : ledgerLines.map((item) => (
                            <tr key={item.journal_line_id}>
                              <td>{formatDate(item.entry_date)}</td>
                              <td>{item.entry_number}</td>
                              <td>{item.description}</td>
                              <td>{item.source_type}</td>
                              <td>{money(item.remaining_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {statementMatches.some((match) => match.voided_at) && (
                      <details className="bank-void-history">
                        <summary>Historial de emparejamientos anulados</summary>
                        <ul>{statementMatches.filter((match) => match.voided_at).map((match) => (
                          <li key={match.id}>#{match.id} · línea {match.statement_line_id} · apunte {match.journal_entry_line_id} · {money(match.amount)} · {match.void_reason || "Sin motivo"}</li>
                        ))}</ul>
                      </details>
                    )}

                    {selectedReconciliation.status === "draft" && canManageBanks && (
                      <button type="button" className="btn-bank-complete"
                        disabled={
                          completingReconciliation || savingStatementLine ||
                          savingMatchId !== null || voidingMatchId !== null ||
                          !reconciliationSummary ||
                          reconciliationSummary.statement_difference == null ||
                          Number(reconciliationSummary.statement_difference) !== 0 ||
                          reconciliationSummary.unmatched_statement_line_count !== 0
                        }
                        onClick={() => completeReconciliation(selectedReconciliation)}>
                        {completingReconciliation ? "Cerrando..." : "Completar conciliación"}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </section>

          <section className="banks-card bank-movements-history">
            <div className="bank-card-heading">
              <div>
                <h2>Movimientos recientes</h2>
                <p>
                  Historial de la cuenta bancaria seleccionada.
                </p>
              </div>
            </div>

            <div className="bank-table-wrapper">
              <table className="bank-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Concepto</th>
                    <th>Referencia</th>
                    <th>Valor</th>
                    <th>Acciones</th>
                  </tr>
                </thead>

                <tbody>
                  {movements.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="bank-empty">
                        No hay movimientos registrados.
                      </td>
                    </tr>
                  ) : (
                    movements.map((movement) => (
                      <tr key={movement.id}>
                        <td>
                          {formatDate(movement.movement_date)}
                        </td>

                        <td>
                          {getMovementLabel(movement.movement_type)}
                        </td>

                        <td>{movement.concept}</td>
                        <td>{movement.reference || "—"}</td>

                        <td
                          className={
                            [
                              "income",
                              "transfer_in",
                              "adjustment_in",
                            ].includes(movement.movement_type)
                              ? "bank-positive"
                              : "bank-negative"
                          }
                        >
                          {[
                            "income",
                            "transfer_in",
                            "adjustment_in",
                          ].includes(movement.movement_type)
                            ? "+"
                            : "-"}

                          {money(movement.amount)}
                        </td>

                        <td>
                          {canViewAccounting && (
                            <button
                              type="button"
                              className="btn-bank-accounting"
                              onClick={() =>
                                handleViewBankMovementJournalEntry(movement)
                              }
                            >
                              Asiento
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <section className="banks-card bank-transfer-card">
        <div className="bank-card-heading">
          <div>
            <h2>Transferencia entre cuentas</h2>
            <p>
              Mueve dinero entre cuentas bancarias sin
              registrarlo como ingreso ni gasto.
            </p>
          </div>
        </div>
        {canManageBanks ? (
          <form className="bank-form" onSubmit={handleTransferSubmit}>
            <div className="bank-form-grid">
              <label>
                Cuenta origen
                <select
                  name="source_bank_account_id"
                  value={transferForm.source_bank_account_id}
                  onChange={handleTransferChange}
                  required
                >
                  <option value="">
                    Selecciona la cuenta origen
                  </option>

                  {accounts
                    .filter((account) => account.is_active)
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.bank_name} — {account.account_number}
                      </option>
                    ))}
                </select>
              </label>

              <label>
                Cuenta destino
                <select
                  name="destination_bank_account_id"
                  value={transferForm.destination_bank_account_id}
                  onChange={handleTransferChange}
                  required
                >
                  <option value="">
                    Selecciona la cuenta destino
                  </option>

                  {accounts
                    .filter(
                      (account) =>
                        account.is_active &&
                        Number(account.id) !==
                        Number(
                          transferForm.source_bank_account_id
                        )
                    )
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.bank_name} — {account.account_number}
                      </option>
                    ))}
                </select>
              </label>

              <label>
                Fecha
                <input
                  type="date"
                  name="transfer_date"
                  value={transferForm.transfer_date}
                  onChange={handleTransferChange}
                  required
                />
              </label>

              <label>
                Valor
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  name="amount"
                  value={transferForm.amount}
                  onChange={handleTransferChange}
                  placeholder="0"
                  required
                />
              </label>
            </div>

            <label className="bank-notes">
              Concepto
              <input
                name="concept"
                value={transferForm.concept}
                onChange={handleTransferChange}
                placeholder="Ejemplo: Traslado a cuenta de ahorros"
                required
              />
            </label>

            <label className="bank-notes">
              Referencia u observaciones
              <textarea
                name="notes"
                value={transferForm.notes}
                onChange={handleTransferChange}
                rows="2"
              />
            </label>

            <button
              type="submit"
              className="btn-bank-save"
              disabled={savingTransfer}
            >
              {savingTransfer
                ? "Registrando..."
                : "Registrar transferencia"}
            </button>
          </form>
        ) : (
          <div className="bank-readonly-message">
            No tienes permiso para administrar cuentas bancarias.
          </div>
        )}
      </section>

      <section className="banks-card bank-scheduled-card">
        <div className="bank-card-heading">
          <div>
            <h2>Pagos programados</h2>
            <p>
              Agenda pagos y ejecútalos cuando sean realizados.
            </p>
          </div>
        </div>
        {canManageBanks ? (
          <form className="bank-form" onSubmit={handleScheduledSubmit}>
            <div className="bank-form-grid">
              <label>
                Cuenta bancaria
                <select
                  name="bank_account_id"
                  value={scheduledForm.bank_account_id}
                  onChange={handleScheduledChange}
                  required
                >
                  <option value="">Selecciona una cuenta</option>

                  {accounts
                    .filter((account) => account.is_active)
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.bank_name} — {account.account_number}
                      </option>
                    ))}
                </select>
              </label>

              <label>
                Proveedor
                <select
                  name="supplier_id"
                  value={scheduledForm.supplier_id}
                  onChange={handleScheduledChange}
                >
                  <option value="">
                    Sin proveedor asociado
                  </option>

                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Fecha de pago
                <input
                  type="date"
                  name="due_date"
                  value={scheduledForm.due_date}
                  onChange={handleScheduledChange}
                  required
                />
              </label>

              <label>
                Valor
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  name="amount"
                  value={scheduledForm.amount}
                  onChange={handleScheduledChange}
                  placeholder="0"
                  required
                />
              </label>
            </div>

            <label className="bank-notes">
              Concepto
              <input
                name="concept"
                value={scheduledForm.concept}
                onChange={handleScheduledChange}
                placeholder="Ejemplo: Pago proveedor mensual"
                required
              />
            </label>

            <label className="bank-notes">
              Referencia u observación
              <textarea
                name="notes"
                value={scheduledForm.notes}
                onChange={handleScheduledChange}
                rows="2"
              />
            </label>

            <button
              type="submit"
              className="btn-bank-save"
              disabled={savingScheduled}
            >
              {savingScheduled
                ? "Guardando..."
                : "Programar pago"}
            </button>
          </form>
        ) : (
          <div className="bank-readonly-message">
            No tienes permiso para administrar cuentas bancarias.
          </div>
        )}

        <div className="bank-table-wrapper">
          <table className="bank-table">
            <thead>
              <tr>
                <th>Vencimiento</th>
                <th>Proveedor</th>
                <th>Concepto</th>
                <th>Valor</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {scheduledPayments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="bank-empty">
                    No hay pagos programados.
                  </td>
                </tr>
              ) : (
                scheduledPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{formatDate(payment.due_date)}</td>
                    <td>
                      {payment.supplier_name || "No aplica"}
                    </td>
                    <td>{payment.concept}</td>
                    <td className="bank-negative">
                      {money(payment.amount)}
                    </td>

                    <td>
                      <span
                        className={`bank-status ${payment.status}`}
                      >
                        {payment.status === "pending"
                          ? "Pendiente"
                          : payment.status === "paid"
                            ? "Pagado"
                            : "Cancelado"}
                      </span>
                    </td>

                    <td>
                      {payment.status === "pending" && (
                        <div className="bank-actions">
                          <button
                            type="button"
                            className="btn-bank-execute"
                            onClick={() =>
                              executeScheduledPayment(payment)
                            }
                          >
                            Ejecutar
                          </button>

                          <button
                            type="button"
                            className="btn-bank-cancel"
                            onClick={() =>
                              cancelScheduledPayment(payment)
                            }
                          >
                            Cancelar
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
      {linkedJournalEntry && (
        <div
          className="bank-linked-entry-backdrop"
          onClick={() => setLinkedJournalEntry(null)}
        >
          <section
            className="bank-linked-entry-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Comprobante contable asociado</h3>

            <p>
              <strong>Número:</strong>{" "}
              {linkedJournalEntry.number}
            </p>

            <p>
              <strong>Fecha:</strong>{" "}
              {new Date(
                `${linkedJournalEntry.entry_date}T00:00:00`
              ).toLocaleDateString("es-CO")}
            </p>

            <p>
              <strong>Descripción:</strong>{" "}
              {linkedJournalEntry.description}
            </p>

            <p>
              <strong>Estado:</strong>{" "}
              {linkedJournalEntry.status}
            </p>

            <div className="bank-linked-entry-actions">
              {canViewAudit && (
                <button
                  type="button"
                  className="btn-bank-accounting"
                  onClick={() =>
                    setAuditJournalEntry(linkedJournalEntry)
                  }
                >
                  Auditoría
                </button>
              )}
              <button
                type="button"
                className="btn-bank-accounting"
                onClick={() =>
                  navigate(`/${company}/contabilidad`)
                }
              >
                Abrir Contabilidad
              </button>

              <button
                type="button"
                className="btn-bank-secondary"
                onClick={() => setLinkedJournalEntry(null)}
              >
                Cerrar
              </button>
            </div>
          </section>
        </div>
      )}
      {auditJournalEntry && (
        <JournalAuditModal
          journalEntry={auditJournalEntry}
          onClose={() => setAuditJournalEntry(null)}
        />
      )}
    </div>
  );
}

export default CompanyBanks;
